import { AnimatePresence, motion } from "framer-motion";
import styles from "../WorkoutPlanDialog.module.scss";
import { IRestTimerState } from "../hooks/useRestTimer";
import { IRestTimerNotificationHint } from "../hooks/useRestTimerNotifications";
import { IosInstallSteps } from "../../../../components/notifications/IosInstallSteps";
import { RestTimerNotificationHint } from "../../../enums";

export type IRestTimerNotificationHintProps = IRestTimerNotificationHint;

const formatMmSs = (seconds: number): string => {
  const safe = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(safe / 60);
  const remainder = safe % 60;
  return `${minutes.toString().padStart(2, "0")}:${remainder.toString().padStart(2, "0")}`;
};

interface IRestTimerCountdownProps {
  state: IRestTimerState;
  onAdjust: (deltaSeconds: number) => void;
  onSkip: () => void;
  /** How to make the timer ring while the phone is locked; null when there is nothing to offer. */
  notificationHint?: IRestTimerNotificationHintProps | null;
}

/**
 * Sticky bottom bar shown while a rest-timer is counting down. Mirrors the
 * UX of the reference screenshot: progress strip on top, big MM:SS readout,
 * -10s / +10s / Skip controls. Below them, when notifications are off on this
 * device, one line on how to make the timer ring while the phone is locked.
 */
export const RestTimerCountdown = ({
  state,
  onAdjust,
  onSkip,
  notificationHint = null,
}: IRestTimerCountdownProps) => {
  const visible = state.isActive;
  const progress = state.totalSeconds > 0
    ? Math.max(0, Math.min(1, state.remainingSeconds / state.totalSeconds))
    : 0;
  // The progress bar should fill from "empty when finished" to "full when
  // started" — invert progress so it represents elapsed-time instead.
  const elapsedFraction = 1 - progress;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className={styles.rest_countdown}
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 60, opacity: 0 }}
          transition={{ duration: 0.22, ease: "easeOut" }}
          role="status"
          aria-live="polite"
        >
          <div
            className={styles.rest_countdown_progress}
            style={{ transform: `scaleX(${elapsedFraction})` }}
          />
          <div className={styles.rest_countdown_label}>
            {state.exerciseName
              ? `Почивка · ${state.exerciseName}`
              : "Почивка"}
          </div>
          <div className={styles.rest_countdown_time}>
            {formatMmSs(state.remainingSeconds)}
          </div>
          <div className={styles.rest_countdown_actions}>
            <button
              type="button"
              className={styles.rest_countdown_adjust}
              onClick={() => onAdjust(-10)}
              aria-label="Намали с 10 секунди"
            >
              -10
            </button>
            <button
              type="button"
              className={styles.rest_countdown_adjust}
              onClick={() => onAdjust(10)}
              aria-label="Добави 10 секунди"
            >
              +10
            </button>
            <button
              type="button"
              className={styles.rest_countdown_skip}
              onClick={onSkip}
              aria-label="Пропусни почивката"
            >
              Пропусни
            </button>
          </div>
          {notificationHint && (
            <div className={styles.rest_countdown_hint}>
              <span className={styles.rest_countdown_hint_text}>
                {notificationHint.view === RestTimerNotificationHint.Enable
                  ? "Включете известията, за да звъни и при заключен телефон."
                  : "Добавете Planny на началния екран, за да звъни и при заключен телефон."}
              </span>
              {notificationHint.view === RestTimerNotificationHint.Enable ? (
                <button
                  type="button"
                  className={styles.rest_countdown_hint_action}
                  onClick={notificationHint.onEnable}
                  disabled={notificationHint.isBusy}
                >
                  Включи
                </button>
              ) : (
                <button
                  type="button"
                  className={styles.rest_countdown_hint_action}
                  onClick={notificationHint.onToggleIosSteps}
                  aria-expanded={notificationHint.isIosStepsOpen}
                >
                  {notificationHint.isIosStepsOpen ? "Скрий" : "Как?"}
                </button>
              )}
              <button
                type="button"
                className={styles.rest_countdown_hint_dismiss}
                onClick={notificationHint.onDismiss}
                aria-label="Не сега"
              >
                ×
              </button>
              {notificationHint.view === RestTimerNotificationHint.IosInstall &&
                notificationHint.isIosStepsOpen && (
                  <div className={styles.rest_countdown_hint_steps}>
                    <IosInstallSteps />
                  </div>
                )}
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
